import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../../components/AthenaPricingEngine/page-header";
import { Panel } from "../../../components/AthenaPricingEngine/panel";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Skeleton } from "../../../ui/skeleton";
import { Switch } from "../../../ui/switch";
import { Button } from "../../../ui/button";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import {
  ActivePolicyTable,
  ActivePolicyTableByIndustry,
  IndustryEnabledPanel,
  IndustryTabs,
  OfficeTabs,
  RbpEnabledPanel,
  RbpToggleDialog,
  SyncStatusFooter,
  TargetApprovalQueue,
  TargetLogTable,
  TargetProposalDialog,
  buildPolicyRows,
  buildPolicyRowsByIndustry,
} from "../../../components/AthenaPricingEngine/manage-targets-panels";
import { hasTier } from "../../../components/AthenaPricingEngine/access-control";
import {
  ALL_INDUSTRIES,
  ALL_OFFICES,
  PAGE_SIZE,
  rbpEnabledFor,
  rbpEnabledForByIndustry,
  resolvePolicy,
  useActivePolicyPage,
  useDecideTargetProposal,
  usePolicyContext,
  useProposeTarget,
  useSyncStatus,
  useTargetLogPage,
  useTargetPolicies,
  useTargetProposals,
  type PolicyRating,
  type PolicyRow,
  type PolicyRowByIndustry,
  type TargetProposal,
} from "../lib/api/manage-targets";
import { useIndustries, useOffices } from "../lib/api/reference";

/** Which dimension governs the ladder shown below -- "office" is the default so
 *  the page behaves exactly as it always has for anyone who never touches the
 *  new control. */
type Ladder = "office" | "industry";

export default function ManageTargets() {
  const [searchParams] = useSearchParams();
  const officesQuery = useOffices();
  const industriesQuery = useIndustries();
  // Manage Targets has no real per-office backing table yet -- policies are
  // keyed by office NAME (see manage-targets.ts), not office_number.
  const offices = (officesQuery.data ?? []).map((o) => o.officeName);
  const industries = industriesQuery.data ?? [];

  const searchOffice = searchParams.get("office") ?? undefined;
  const searchIndustry = searchParams.get("industry") ?? undefined;
  const deepLinkOffice = searchOffice && offices.includes(searchOffice)
    ? searchOffice
    : undefined;
  const deepLinkIndustry = searchIndustry && industries.includes(searchIndustry)
    ? searchIndustry
    : undefined;

  // Which ladder governs the panel/table/queue/log below. Deep-linking an office keeps
  // the page on the office-led ladder even if an industry is also present in the URL --
  // that combination already means "open this office's dialog pre-filled with this
  // industry" (see the effect below), not "switch to the industry-led view".
  const [ladder, setLadder] = useState<Ladder>("office");
  const [office, setOffice] = useState(deepLinkOffice ?? ALL_OFFICES);
  const [industry, setIndustry] = useState(deepLinkIndustry ?? ALL_INDUSTRIES);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogScope, setDialogScope] = useState<{
    office: string;
    industry?: string;
    rating?: PolicyRating;
  }>({
    office: deepLinkOffice ?? offices[0] ?? "",
  });
  const [lockOffice, setLockOffice] = useState(false);
  const [secondApproval, setSecondApproval] = useState(false);
  const [thresholdBps, setThresholdBps] = useState("25");
  const [rbpToggle, setRbpToggle] = useState<{
    /** Which ladder this toggle belongs to -- decides which of office/industry below is
     *  the dominant (always-present) name and which is the optional narrower one. */
    primary: Ladder;
    office?: string;
    industry?: string;
    current: boolean;
    proposed: boolean;
  } | null>(null);

  // Reviewer raises a change, contributor decides it -- the same split the backend
  // enforces on POST /target-proposals and .../decide.
  const canPropose = hasTier("reviewer");
  const canDecide = hasTier("contributor");

  const policies = useTargetPolicies();
  const proposals = useTargetProposals();
  const sync = useSyncStatus();
  const propose = useProposeTarget();
  const decide = useDecideTargetProposal();
  const policyContext = usePolicyContext();

  // Active Policy's "All Offices"/"All Industries" aggregate view has no
  // natural row-count ceiling (every office × every industry it has data
  // for), so it's server-paginated -- a specific office/industry tab is
  // already small and bounded and keeps building from the plain
  // `policies`/`policyContext` fetch above via buildPolicyRows below instead.
  const showServerPagedOfficeRows = ladder === "office" && office === ALL_OFFICES;
  const showServerPagedIndustryRows = ladder === "industry" && industry === ALL_INDUSTRIES;
  const activePolicyPage = useActivePolicyPage(ladder, showServerPagedOfficeRows || showServerPagedIndustryRows);

  // Permanent Log is append-only and never deleted, so it has no ceiling
  // either -- scoped server-side to whatever's currently selected (undefined
  // means "All Offices"/"All Industries", i.e. no filter) instead of
  // client-filtering an unbounded fetch down to a shrinking page. Prev/Next
  // over a plain offset, same pattern as portfolio-risk-concentrations'
  // FairValueLog, rather than an accumulating "load more".
  const logScopeOffice = ladder === "office" && office !== ALL_OFFICES ? office : undefined;
  const logScopeIndustry = ladder === "industry" && industry !== ALL_INDUSTRIES ? industry : undefined;
  const [logOffset, setLogOffset] = useState(0);
  useEffect(() => {
    setLogOffset(0);
  }, [logScopeOffice, logScopeIndustry]);
  const log = useTargetLogPage(logOffset, logScopeOffice, logScopeIndustry);

  useEffect(() => {
    if (!deepLinkOffice) return;
    setOffice(deepLinkOffice);
    setDialogScope({
      office: deepLinkOffice,
      ...(searchIndustry ? { industry: searchIndustry } : {}),
    });
    setLockOffice(true);
    setDialogOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkOffice, searchIndustry]);

  useEffect(() => {
    // An office deep-link takes priority (it opens the propose dialog above) -- an
    // industry-only link switches straight to the industry-led ladder on that industry's
    // tab, mirroring what an office-only link does for the office-led one.
    if (!deepLinkIndustry || deepLinkOffice) return;
    setLadder("industry");
    setIndustry(deepLinkIndustry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkIndustry, deepLinkOffice]);

  const clientRows = useMemo(
    () =>
      showServerPagedOfficeRows
        ? []
        : buildPolicyRows(policies.data ?? [], policyContext.data ?? [], office, offices, industries),
    [showServerPagedOfficeRows, policies.data, policyContext.data, office, offices, industries],
  );
  const rows: PolicyRow[] = showServerPagedOfficeRows
    ? (activePolicyPage.rows as PolicyRow[])
    : clientRows;

  const clientRowsByIndustry = useMemo(
    () =>
      showServerPagedIndustryRows
        ? []
        : buildPolicyRowsByIndustry(
            policies.data ?? [],
            policyContext.data ?? [],
            industry,
            offices,
            industries,
          ),
    [showServerPagedIndustryRows, policies.data, policyContext.data, industry, offices, industries],
  );
  const rowsByIndustry: PolicyRowByIndustry[] = showServerPagedIndustryRows
    ? (activePolicyPage.rows as PolicyRowByIndustry[])
    : clientRowsByIndustry;

  const activePolicyLoading =
    showServerPagedOfficeRows || showServerPagedIndustryRows ? activePolicyPage.isLoading : policies.isLoading;
  const activePolicyIsError =
    showServerPagedOfficeRows || showServerPagedIndustryRows ? activePolicyPage.isError : policies.isError;

  // Surfaced by IndustryEnabledPanel as a plain note, not a gate -- see that component's
  // own doc comment on why a pre-existing office-led decision on this industry's rows is
  // worth confirming rather than assuming it means the same thing under this ladder.
  const industryHasOwnOfficeOverrides =
    industry !== ALL_INDUSTRIES &&
    (policies.data ?? []).some(
      (p) => p.office !== ALL_OFFICES && p.industry === industry && p.rbpEnabled != null,
    );


  // "Current" is what pricing applies to this scope RIGHT NOW, which with a
  // sparse grid means the resolved value -- inherited from a broader row when
  // the scope has none of its own. That is the number the proposal is moving
  // away from, so it is what belongs in the dialog and in the log's old-value
  // columns; showing "no prior value" for an inherited scope was misleading.
  function currentFor(div: string, industry?: string, rating?: PolicyRating) {
    const resolved = resolvePolicy(policies.data ?? [], div, industry, rating);
    return {
      opex: resolved?.opex ?? null,
      targetNetMargin: resolved?.targetNetMargin ?? null,
      internalCommissionRate: resolved?.internalCommissionRate ?? null,
    };
  }

  const scopedProposals = (proposals.data ?? []).filter((proposal) =>
    ladder === "office"
      ? office === ALL_OFFICES || proposal.office === office
      : industry === ALL_INDUSTRIES || proposal.industry === industry,
  );

  const threshold = Number(thresholdBps);

  function requiresSecondApproval(proposal: TargetProposal) {
    if (!secondApproval || !Number.isFinite(threshold)) return false;
    // RBP on/off is never forced into a second approval.
    if (proposal.kind === "rbp-toggle") return false;
    const opexMove = Math.abs(proposal.proposedOpex - (proposal.currentOpex ?? 0)) * 100;
    const marginMove =
      Math.abs(proposal.proposedTargetNetMargin - (proposal.currentTargetNetMargin ?? 0)) * 100;
    return Math.max(opexMove, marginMove) > threshold;
  }

  return (
    <div>
      <PageHeader
        title="Manage Targets"
        subtitle="Propose and approve OpEx, target net margin and internal commission rate"
      />

      <div className="mt-8 flex flex-wrap items-center gap-2">
        <ToggleGroup
          type="single"
          value={ladder}
          onValueChange={(value) => value && setLadder(value as Ladder)}
          className="w-fit border border-border bg-secondary"
        >
          <ToggleGroupItem value="office" className="h-8 px-3 text-xs">
            By Office
          </ToggleGroupItem>
          <ToggleGroupItem value="industry" className="h-8 px-3 text-xs">
            By Industry
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="mt-4">
        {ladder === "office" ? (
          <OfficeTabs value={office} onChange={setOffice} offices={offices} />
        ) : (
          <IndustryTabs value={industry} onChange={setIndustry} industries={industries} />
        )}
      </div>

      {ladder === "office" ? (
        office !== ALL_OFFICES ? (
          <div className="mt-6">
            <RbpEnabledPanel
              office={office}
              canPropose={canPropose}
              enabled={rbpEnabledFor(policies.data ?? [], office)}
              onPropose={(div, next) =>
                setRbpToggle({
                  primary: "office",
                  office: div,
                  current: rbpEnabledFor(policies.data ?? [], div),
                  proposed: next,
                })
              }
            />
          </div>
        ) : null
      ) : industry !== ALL_INDUSTRIES ? (
        <div className="mt-6">
          <IndustryEnabledPanel
            industry={industry}
            canPropose={canPropose}
            enabled={rbpEnabledForByIndustry(policies.data ?? [], industry)}
            hasOwnOfficeOverrides={industryHasOwnOfficeOverrides}
            onPropose={(ind, next) =>
              setRbpToggle({
                primary: "industry",
                industry: ind,
                current: rbpEnabledForByIndustry(policies.data ?? [], ind),
                proposed: next,
              })
            }
          />
        </div>
      ) : null}

      <Panel
        className="mt-6"
        title="Active Policy"
        description="FTP, expected loss and external commission are read-only context. Gross margin grosses the target net margin up for internal commission; required yield adds it all together."
        right={
          canPropose ? (
            <Button
              size="sm"
              className="h-9 text-xs"
              onClick={() => {
                if (ladder === "office") {
                  setDialogScope({
                    office: office === ALL_OFFICES ? offices[0] ?? "" : office,
                  });
                } else {
                  setDialogScope({
                    office: ALL_OFFICES,
                    ...(industry !== ALL_INDUSTRIES ? { industry } : {}),
                  });
                }
                setLockOffice(false);
                setDialogOpen(true);
              }}
            >
              Propose Change
            </Button>
          ) : null
        }
      >
        {activePolicyIsError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not load target policy.
          </div>
        ) : activePolicyLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : ladder === "office" ? (
          <ActivePolicyTable
            rows={rows}
            canPropose={canPropose}
            context={policyContext.data ?? []}
            policies={policies.data ?? []}
            onPropose={(div, ind, rating) => {
              setDialogScope({
                office: div,
                ...(ind ? { industry: ind } : {}),
                ...(rating ? { rating } : {}),
              });
              setLockOffice(false);
              setDialogOpen(true);
            }}
            onToggleRbp={(div, ind, next) =>
              setRbpToggle({
                primary: "office",
                office: div,
                ...(ind ? { industry: ind } : {}),
                current: rbpEnabledFor(policies.data ?? [], div, ind),
                proposed: next,
              })
            }
          />
        ) : (
          <ActivePolicyTableByIndustry
            rows={rowsByIndustry}
            canPropose={canPropose}
            context={policyContext.data ?? []}
            policies={policies.data ?? []}
            onPropose={(ind, off, rating) => {
              setDialogScope({
                office: off ?? ALL_OFFICES,
                industry: ind,
                ...(rating ? { rating } : {}),
              });
              setLockOffice(false);
              setDialogOpen(true);
            }}
            onToggleRbp={(ind, off, next) =>
              setRbpToggle({
                primary: "industry",
                industry: ind,
                ...(off ? { office: off } : {}),
                current: rbpEnabledForByIndustry(policies.data ?? [], ind, off),
                proposed: next,
              })
            }
          />
        )}
        {(showServerPagedOfficeRows || showServerPagedIndustryRows) && activePolicyPage.hasNextPage ? (
          <div className="mt-4 flex justify-center">
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              disabled={activePolicyPage.isFetchingNextPage}
              onClick={() => activePolicyPage.fetchNextPage()}
            >
              {activePolicyPage.isFetchingNextPage ? "Loading…" : "Load Next 10"}
            </Button>
          </div>
        ) : null}
      </Panel>

      <Panel
        className="mt-6"
        title="Approval Queue"
        description={
          ladder === "office"
            ? office === ALL_OFFICES
              ? "All offices"
              : `Pending changes in ${office}`
            : industry === ALL_INDUSTRIES
              ? "All industries"
              : `Pending changes in ${industry}`
        }
      >
        <div className="mb-4 flex flex-wrap items-end gap-6 border border-border bg-secondary/30 px-3 py-3">
          <div className="flex items-center gap-3">
            <Switch
              id="second-approval"
              checked={secondApproval}
              onCheckedChange={setSecondApproval}
            />
            <Label htmlFor="second-approval" className="text-xs">
              Require a second approval above
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Input
              aria-label="Second Approval Threshold (bps)"
              inputMode="numeric"
              value={thresholdBps}
              disabled={!secondApproval}
              onChange={(event) => setThresholdBps(event.target.value)}
              className="h-8 w-24 text-sm tabular-nums"
            />
            <span className="text-xs text-muted-foreground">bps of movement</span>
          </div>
        </div>

        {proposals.isError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not load the approval queue.
          </div>
        ) : proposals.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : (
          <TargetApprovalQueue
            proposals={scopedProposals}
            canDecide={canDecide}
            requiresSecondApproval={requiresSecondApproval}
            isDeciding={decide.isPending}
            onDecide={(id, status, notes) => decide.mutateAsync({ id, status, notes })}
          />
        )}
      </Panel>

      <Panel
        className="mt-6"
        title="Permanent Log"
        description="Newest first. Nothing is ever deleted."
      >
        {log.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : (
          <>
            <TargetLogTable entries={log.entries} />
            <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {log.total === 0
                  ? "0 of 0"
                  : `${logOffset + 1}–${Math.min(logOffset + PAGE_SIZE, log.total)} of ${log.total}`}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  disabled={logOffset === 0}
                  onClick={() => setLogOffset((o) => Math.max(0, o - PAGE_SIZE))}
                >
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  disabled={logOffset + PAGE_SIZE >= log.total}
                  onClick={() => setLogOffset((o) => o + PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Panel>

      <SyncStatusFooter sync={sync.data} />

      <TargetProposalDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={dialogScope}
        lockOffice={lockOffice}
        currentFor={currentFor}
        defaultsFor={(div) => {
          const fallback = resolvePolicy(policies.data ?? [], div);
          return fallback
            ? {
                opex: fallback.opex,
                targetNetMargin: fallback.targetNetMargin,
                internalCommissionRate: fallback.internalCommissionRate,
              }
            : undefined;
        }}
        isSubmitting={propose.isPending}
        offices={offices}
        industries={industries}
        context={policyContext.data ?? []}
        onSubmit={async (draft) => {
          await propose.mutateAsync(draft);
          setDialogOpen(false);
        }}
      />

      <RbpToggleDialog
        open={rbpToggle != null}
        onOpenChange={(open) => !open && setRbpToggle(null)}
        primary={rbpToggle?.primary ?? "office"}
        {...(rbpToggle?.office ? { office: rbpToggle.office } : {})}
        {...(rbpToggle?.industry ? { industry: rbpToggle.industry } : {})}
        currentEnabled={rbpToggle?.current ?? true}
        proposedEnabled={rbpToggle?.proposed ?? true}
        isSubmitting={propose.isPending}
        onSubmit={async (draft) => {
          await propose.mutateAsync(draft);
          setRbpToggle(null);
        }}
      />
    </div>
  );
}
